import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage, ForgotPasswordPage, ResetPasswordPage, RegisterPage } from './pages/AuthPages';
import WelcomePage from './pages/WelcomePage';
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TrainingModule from './pages/TrainingModule';
import OnboardingDashboard from './pages/OnboardingDashboard';
import OnboardingSection from './pages/OnboardingSection';
import AdminOnboarding from './pages/AdminOnboarding';
import SCRPage from './pages/SCRPage';
import ProfilePage from './pages/ProfilePage';
import AdminTutorManagement from './pages/AdminTutorManagement';
import AdminScheduling from './pages/AdminScheduling';
import ContractPage from './pages/ContractPage';
import MySchedulePage from './pages/MySchedulePage';
import AvailableShiftsPage from './pages/AvailableShiftsPage';
import AdminTimesheets from './pages/AdminTimesheets';
import MyTimesheetPage from './pages/MyTimesheetPage';
import SetupPage from './pages/SetupPage';
import './index.css';

// Redirect component for authenticated users
const AuthRedirect = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Welcome/Landing page */}
      <Route path="/" element={
        <AuthRedirect>
          <WelcomePage />
        </AuthRedirect>
      } />
      
      {/* Auth routes */}
      <Route path="/login" element={
        <AuthRedirect>
          <LoginPage />
        </AuthRedirect>
      } />
      <Route path="/forgot-password" element={
        <AuthRedirect>
          <ForgotPasswordPage />
        </AuthRedirect>
      } />
      <Route path="/register" element={
        <AuthRedirect>
          <RegisterPage />
        </AuthRedirect>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup" element={<SetupPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      <Route path="/training/:moduleId" element={
        <ProtectedRoute>
          <TrainingModule />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/onboarding" element={
        <ProtectedRoute adminOnly>
          <AdminOnboarding />
        </ProtectedRoute>
      } />
      <Route path="/admin/scr" element={
        <ProtectedRoute adminOnly>
          <SCRPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/tutors" element={
        <ProtectedRoute adminOnly>
          <AdminTutorManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/scheduling" element={
        <ProtectedRoute adminOnly>
          <AdminScheduling />
        </ProtectedRoute>
      } />
      <Route path="/admin/timesheets" element={
        <ProtectedRoute adminOnly>
          <AdminTimesheets />
        </ProtectedRoute>
      } />
      
      {/* Onboarding routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingDashboard />
        </ProtectedRoute>
      } />
      <Route path="/onboarding/:sectionId" element={
        <ProtectedRoute>
          <OnboardingSection />
        </ProtectedRoute>
      } />
      
      {/* Contract */}
      <Route path="/contract" element={
        <ProtectedRoute>
          <ContractPage />
        </ProtectedRoute>
      } />
      
      {/* My Schedule */}
      <Route path="/my-schedule" element={
        <ProtectedRoute>
          <MySchedulePage />
        </ProtectedRoute>
      } />
      
      {/* Available Shifts (Standby) */}
      <Route path="/available-shifts" element={
        <ProtectedRoute>
          <AvailableShiftsPage />
        </ProtectedRoute>
      } />
      
      {/* Timesheet & Pay */}
      <Route path="/my-timesheet" element={
        <ProtectedRoute>
          <MyTimesheetPage />
        </ProtectedRoute>
      } />
      
      {/* Profile */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
